import { LegalPage, Section, Bullets, CONTACT_EMAIL } from "@/components/LegalLayout";

export default function Privacy() {
  return (
    <LegalPage
      testid="privacy-page"
      title="Privacy Policy"
      subtitle="How REAM handles the information you and your team put into the platform."
    >
      <Section heading="1. Who we are">
        <p>
          REAM (Real Estate Asset Management) is a private, invitation-only platform used by a
          landholding business to maintain the document repository and site-monitoring records of
          its own agricultural, commercial and residential properties, primarily in Telangana,
          India. Accounts are created by an administrator; there is no public sign-up.
        </p>
      </Section>

      <Section heading="2. Information we hold">
        <Bullets items={[
          "Account details: name, work email, phone number, role (administrator or field executive) and a securely hashed password. We never store passwords in readable form.",
          "Property records: survey numbers, extent, ownership and khata details, addresses, district and police-station jurisdiction, acquisition values, vaastu notes, litigation and mortgage particulars.",
          "Documents you upload: title deeds, encumbrance certificates, tax receipts, lease agreements, legal opinions, survey drawings, insurance policies and similar files.",
          "Site-visit evidence: photographs and videos captured by field executives together with their GPS coordinates, accuracy, device timestamp and observation notes.",
          "Optional CCTV configuration: camera hostname, port, channel and credentials, which are encrypted before storage. Live frames are relayed to your browser and are not retained on our servers.",
          "Usage and audit records: who signed in, and who created, changed, downloaded, shared or deleted a record, with a timestamp and originating IP address.",
        ]} />
      </Section>

      <Section heading="3. Why we hold it">
        <p>
          Information is processed solely to operate the platform for the property owner: to keep an
          organised repository, to verify that properties are physically inspected on schedule, to
          alert the right people about court hearings, document expiries and inspection lapses, to
          share specific records with authorised third parties at the owner's instruction, and to
          maintain an audit trail for governance. We do not use your data to build advertising
          profiles and we do not sell or rent it to anyone.
        </p>
      </Section>

      <Section heading="4. Emails we send">
        <p>
          The platform sends operational email only — monthly inspection reminders to field
          executives, weekly portfolio digests and escalation notices to administrators, court
          hearing reminders, document renewal alerts, backup summaries and read-only sharing links
          you explicitly ask us to send. These are service messages, not marketing.
        </p>
      </Section>

      <Section heading="5. Read-only sharing links">
        <p>
          An administrator may generate a link that lets a bank, advocate or auditor view one
          property's records without logging in. Every link carries an expiry date chosen at
          creation, can be revoked at any time, and records each view. Anyone holding the link
          before expiry can see the selected records, so please share it only with the intended
          recipient. Recipients of an emailed link are recorded in the audit trail.
        </p>
      </Section>

      <Section heading="6. Third-party services we rely on">
        <Bullets items={[
          "Cloud hosting and managed database, which store the platform's data.",
          "Object storage, which holds uploaded documents and site media.",
          "Gmail SMTP, used to deliver the operational email described above.",
          "Google Drive, used only if an administrator connects it, to store monthly off-site backups in a folder belonging to that Google account.",
          "OpenStreetMap tile servers, which render the portfolio map in your browser.",
        ]} />
      </Section>

      <Section heading="7. Google Drive access and Limited Use">
        <p>
          If an administrator chooses to connect Google Drive, REAM requests permission to create
          and manage files in that Google account's Drive for one purpose: writing monthly backup
          archives of your own property records into the designated backup folder, and deleting
          backup folders older than the retention window.
        </p>
        <p>
          REAM's use of information received from Google APIs adheres to the Google API Services
          User Data Policy, including the Limited Use requirements. Specifically, we do not read,
          index or analyse your existing Drive content, we do not transfer Google user data to any
          third party except as needed to provide this backup feature, we do not use it for
          advertising, and no human reads it. The connection can be withdrawn at any time from the
          Notifications page in REAM, or from your Google Account's third-party access settings, and
          revoking it stops all further Drive access immediately.
        </p>
      </Section>

      <Section heading="8. How long we keep information">
        <p>
          Property records, documents and inspection evidence are retained for as long as the owner
          maintains the account, because they form a permanent asset register. Audit entries are
          retained for the life of the account. Off-site backups on Google Drive are pruned
          automatically beyond the configured retention window. Deleting a property removes its
          inspection history and hides its documents from the application.
        </p>
      </Section>

      <Section heading="9. How we protect it">
        <p>
          Access requires a password and a signed session token, and permissions are enforced by
          role — a field executive can only see the properties assigned to them. All traffic is
          encrypted in transit over HTTPS. Passwords are hashed, and camera credentials are
          encrypted at rest. Sharing links are unguessable, time-bound and revocable. No system is
          perfectly secure, so please keep your password confidential and tell us at once if you
          suspect misuse.
        </p>
      </Section>

      <Section heading="10. Your rights">
        <p>
          Consistent with India's Digital Personal Data Protection Act, 2023, you may ask us to
          confirm what personal information we hold about you, correct it if it is inaccurate, or
          erase it where we are not required to keep it. Because accounts are issued by the property
          owner, please raise such requests through your administrator or write to us directly.
        </p>
      </Section>

      <Section heading="11. Children">
        <p>REAM is a business tool and is not intended for anyone under 18.</p>
      </Section>

      <Section heading="12. Changes and contact">
        <p>
          If this policy changes we will update the date at the top of this page. For any privacy
          question, request or grievance, write to{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary hover:underline">{CONTACT_EMAIL}</a>.
          We aim to respond within 30 days.
        </p>
      </Section>
    </LegalPage>
  );
}
